import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { App, Divider, Flex, Modal, Space, Spin, Typography } from "antd"
import cls from "classname"
import { useTranslation } from "react-i18next"
import { connect, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"

import Button from "../../Components/Button"
import EmptyComponent from "../../Components/Empty"
import ExportDropdown from "../../Components/Export"
import Filters from "../../Components/Filters"
import DropdownDateBlock from "../../Components/Filters/DropdownDateBlock/DropdownDateBlock"
import GlobalFilterTestData from "../../Components/GlobalFilterTestData/globalFilterTest"
import ManageColumn from "../../Components/ManageColumns"
import { CommentModal } from "../../Components/Modal/CommentModal"
import Table from "../../Components/Table"
import { getBaseColumns } from "../../Components/Table/baseColumns"
import Icon from "../../Icon"
import { modalConfig } from "../../theme"
import { adminRoleId } from "../../Utils/permission"
import { configureDate, getCalculatedPeriod, serializeData } from "../../Utils/utils"
import GlobalCreators from "../GlobalFilters/reducer"
import { userSave } from "../User/redux/user.thunks"
import Chart from "./components/Chart"
import { getActionColumn, getColumns, getDefaultColumn } from "./components/Table/columns"
import Creators, { selectAreFiltersDefault } from "./reducer"

import "./styles.less"

const pageSize = 50

const { Title, Text } = Typography

const BusinessAccountsPage = ({
  isMobile,
  isAdmin,
  businessAccountId,
  roleId,
  visibleFiltersAdmin,
  defaultColumnsAdmin,
  columnsAdmin,
  fetching,
  result,
  filters,
  filterOptions,
  exportFetching,
  exportResult,
  chartLoading,
  statistics,
  chartData,
  baCommentData,
  fetchingBaComment,
  test_data,
  ...props
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { notification, modal } = App.useApp()

  //['7 days', '30 days', 'all', 'custom']
  const [selectPeriod, setSelectPeriod] = useState("7 days")
  const [selectBusinessId, setSelectBusinessId] = useState(null)

  const [pageNumber, setPageNumber] = useState(1)
  const [ordering, setOrdering] = useState(null)
  const [modalType, setModalType] = useState(null)

  const isFiltersDefault = useSelector(selectAreFiltersDefault)

  const { count, results } = result

  const myColumns = columnsAdmin
  const allFilters = defaultColumnsAdmin
  const visibleFilters = visibleFiltersAdmin

  const myColumnsLength = myColumns.filter(({ active }) => active).length
  const myColumnsHash = myColumns
    .filter(({ active }) => active)
    .map(({ field_id }) => field_id)
    .join("")

  const notificationConfig = {
    closeIcon: <Text>{t("Dismiss")}</Text>,
    style: { minWidth: "max-content", width: 475 }
  }

  useEffect(() => {
    document.title = "Business accounts - Chargeback"
    props.getBusinessAccountsFilterOptions()

    return () => {
      props.clearBaCommentsData()
    }
  }, [])

  useEffect(() => {
    const serializeFilters = serializeData(filters)
    const columns = myColumns.filter(({ active }) => active).map(({ field_id }) => field_id)
    const data = {
      page: pageNumber,
      page_size: pageSize,
      ordering,
      ...serializeFilters,
      test_data,
      ...(columns.length && {
        columns: ["id", "status", "brand", "user_id", ...columns].join(",")
      })
    }
    props.getAdminBusinessAccounts({ ...data })
  }, [businessAccountId, ordering, pageNumber, filters, test_data, myColumnsLength])

  useEffect(() => {
    if (selectPeriod && selectBusinessId) {
      let data
      if (selectPeriod.type === "custom") {
        data = configureDate(selectPeriod.type, selectPeriod.period)
      } else data = configureDate(selectPeriod)

      const serializeFilters = serializeData(data)
      props.getAlertsChart({
        business_id: selectBusinessId,
        ...serializeFilters
      })
    }
  }, [selectPeriod, selectBusinessId])

  useEffect(() => {
    if (exportResult) {
      notification.success({
        message: t("Exporting"),
        description:
          t(`${exportResult?.message}`) || t("Business accounts will be downloaded shortly."),
        ...notificationConfig
      })
      props.clearExportResult()
    }
  }, [exportResult])

  const tableSettings = useMemo(
    () => ({
      scroll: {
        //x: (230 * myColumns.filter(({active}) => active).length),
        x: "max-content",
        y: "calc(100vh - 72px - 24px - 32px - 24px - 43px - (16px + 32px + 16px))",
        scrollToFirstRowOnChange: true
      },
      pagination: {
        total: count,
        current: pageNumber,
        pageSize: pageSize
      }
    }),
    [myColumnsLength, count, pageNumber]
  )

  const getTableColumns = useMemo(() => {
    let allColumns = {
      ...getBaseColumns({ t, isAdmin: true, onSelectRow: handleSelectRow }),
      ...getColumns({ onSelectRow: handleSelectRow })
    }

    return [
      allColumns["status"],
      ...myColumns
        .filter(({ active }) => active)
        .map((data) => allColumns?.[data.field_id] || getDefaultColumn({ ...data, t })),
      getActionColumn({ onSelectRow: handleSelectRow })
    ]
  }, [myColumnsHash])

  const handleChangeTable = useCallback((pagination, filters, sorter) => {
    let ordering = null
    if (sorter) {
      if (Array.isArray(sorter)) {
        ordering = sorter
          ?.map(({ field, order }) => (order === "ascend" ? field : `-${field}`))
          .join(",")
      } else {
        if (sorter.order) {
          ordering = sorter.order === "ascend" ? sorter.field : `-${sorter.field}`
        }
      }
    }
    setPageNumber(pagination.current)
    setOrdering(ordering)
  }, [])

  const handleChangeFilter = useCallback((newData, type) => {
    if (newData?.user_email) {
      if (Array.isArray(newData.user_email) && newData.user_email.length > 0) {
        newData.user_email.forEach((email) => {
          if (email) props.addGlobalFilter("user_email", email.trim())
        })
      } else if (typeof newData.user_email === "string" && newData.user_email.trim()) {
        props.addGlobalFilter("user_email", newData.user_email.trim())
      } else {
        props.removeGlobalFilter("user_email")
      }
    }
    if (type === "changeVisible") {
      props.changeVisibleFilters("visibleFiltersAdmin", newData)
    } else {
      props.changeFilters(newData)
      setPageNumber(1)
    }
  }, [])

  const onClear = useCallback(() => {
    props.clearBusinessAccountsFilters()
    props.clearGlobalFilters()
  }, [])

  const handleChangeCustomDate = useCallback((newFilters) => {
    if (newFilters?.customPeriod) {
      setSelectPeriod({
        type: "custom",
        period: newFilters.customPeriod,
        presetId: newFilters?.__preset_customPeriod ?? null
      })
    } else {
      setTimeout(() => setSelectPeriod("30 days"), 100)
    }
  }, [])

  const handleChangeColumns = useCallback((newColumns) => {
    props.changeColumns("columnsAdmin", newColumns)
  }, [])

  function handleSelectRow(recordData, type) {
    let business_account_id =
      recordData?.business_account?.business_account_id || recordData?.business_account?.id
    if (type === "settings" || type === "business_account") {
      if (business_account_id) {
        const query = new URLSearchParams({ business_account_id })
        if (type === "settings") {
          navigate(`/business_settings/enrollment?${query}`)
        } else {
          const localTestingUri = "http://localhost:3000"
          const isLocalHost = window.location.origin === localTestingUri
          window.open(
            `${
              isLocalHost ? localTestingUri : process.env.REACT_APP_BASE_URI
            }/business_settings/enrollment?${query}`
          )
        }
      }
      //setRedirect('/business_settings/my_business')
    } else if (type === "dashboard") {
      navigate(`/admin/dashboard?business_account_id=${business_account_id}`)
    } else if (type === "alerts_count") {
      if (business_account_id) {
        setSelectBusinessId(business_account_id)
      }
      //setRedirect('/integrations')
    } else if (type === "delete") {
      modal.confirm({
        ...modalConfig("exclamation"),
        centered: !isMobile,
        //transitionName: '',

        title: t("Confirm deleting"),
        content: t(
          "Are you sure you want to delete this business account? This action can NOT be undone."
        ),

        cancelText: t("Cancel"),
        okText: t("Delete"),
        okButtonProps: {
          ...modalConfig("exclamation")?.okButtonProps,
          danger: true,
          ghost: true
        },
        onOk() {
          props.deleteUserBusinessAccount(business_account_id)
        },
        onCancel() {
          console.log("Cancel")
        }
      })
    } else if (type === "user_balance") {
      navigate(`/settings/balance?user_id=${recordData?.user_id}`, {
        state: { from: "/business_accounts" }
      })
    } else if (type?.startsWith("updated_bh:") && business_account_id) {
      const source = type.split(":")[1]
      const localTestingUri = "http://localhost:3000"
      const isLocalHost = window.location.origin === localTestingUri
      window?.open(
        `${
          isLocalHost ? localTestingUri : process.env.REACT_APP_BASE_URI
        }/business_settings/business_health/overview?${new URLSearchParams({
          business_account_id,
          ...(source ? { source } : {})
        })}`
      )
    } else if (["comments_ba_page"].includes(type)) {
      setModalType({
        type,
        data: recordData
      })
      props.getBaCommentData({ ba_id: recordData?.business_account?.id })
    }
  }

  const handleExport = useCallback(
    (type) => {
      const serializeFilters = serializeData(filters)
      props.getBusinessAccountsExport({
        file_name: "Users report",
        ...(type !== "all" && serializeFilters),
        ...(type !== "all" ? { test_data } : {})
      })
    },
    [filters, businessAccountId, test_data]
  )

  const handleChangePeriod = (newPeriod) => {
    setSelectPeriod(newPeriod)
  }

  //actions for commentModal
  const formRef = useRef(null)
  const [modalTabKey, setModalTabKey] = useState("business_account")
  const selectedBusinessAccountId = modalType?.data?.business_account?.id
  const activeCommentData =
    modalTabKey === "business_accounts_owner" ? baCommentData?.comment : baCommentData

  // Switching tabs re-mounts CommentForm before the new tab's comment has actually loaded,
  // so without this it briefly (and, depending on how fast the fetch resolves, unpredictably)
  // shows the previous tab's still-cached baCommentData instead of a blank/loading field.
  const handleModalTabChange = (key) => {
    setModalTabKey(key)
    props.resetBaCommentData()
  }

  const onSaveFormSuccess = () => {
    setModalType(false) //close modal
    // Match onClose: without this, modalTabKey carries over to the next time this modal
    // opens (for this BA or another) and reopens already pointed at the owner tab.
    setModalTabKey("business_account")
    props.clearBaCommentsData()
    notification.success({
      message: "Save Comment",
      description: "Comment has been saved successfully.",
      ...notificationConfig
    })
  }

  const onCommentDeleteSuccess = () => {
    notification.success({
      message: "Delete Comment",
      description: "Comment has been deleted successfully.",
      ...notificationConfig
    })
    formRef.current?.setFieldsValue({ comment: "" })
  }

  const onDeleteCommentAttachmentSuccess = () => {
    notification.success({
      message: "Delete Comment",
      description: "Comment has been deleted successfully.",
      ...notificationConfig
    })
    props.getBaCommentData({
      ba_id: selectedBusinessAccountId,
      ...(modalTabKey === "business_accounts_owner" && { owner: true })
    })
  }

  const handleSaveCommentForm = (data, type) => {
    const newData = {
      ...data,
      ba_id: selectedBusinessAccountId,
      comment_id: activeCommentData?.id,
      owner: modalTabKey === "business_accounts_owner",
      onSuccess: onSaveFormSuccess
    }
    props.saveBaComment(newData)
  }

  const onCommentDelete = (data, formTypeData) => {
    props.deleteBaComment({
      ba_id: selectedBusinessAccountId,
      owner: modalTabKey === "business_accounts_owner",
      onSuccess: onCommentDeleteSuccess
    })
  }

  const onDeleteAttachment = (data) => {
    props.deleteBaCommentAttachment({
      id: data?.id, //attachment_id
      ba_id: selectedBusinessAccountId,
      comment_id: activeCommentData?.id,
      onSuccess: onDeleteCommentAttachmentSuccess
    })
  }

  useEffect(() => {
    if (modalTabKey === "business_accounts_owner") {
      props.getBaCommentData({ ba_id: selectedBusinessAccountId, owner: true })
    }
  }, [modalTabKey, selectedBusinessAccountId])

  // Symmetric to the effect above: switching back to the BA tab has to refetch too,
  // otherwise it keeps showing whatever the owner tab last loaded into the shared
  // baCommentData.
  useEffect(() => {
    if (modalTabKey === "business_account" && selectedBusinessAccountId) {
      props.getBaCommentData({ ba_id: selectedBusinessAccountId })
    }
  }, [modalTabKey, selectedBusinessAccountId])

  return (
    <div className="users-page">
      <Title level={3} style={{ textAlign: "left", marginBottom: 16 }}>
        {`${t("Business accounts")}: ${statistics?.["business_accounts_counter"]}`}
      </Title>

      <Divider />

      <Flex gap={32} align="center" justify="space-between" style={{ margin: "-4px 0 20px" }}>
        <Filters
          visibleFilters={visibleFilters}
          filterOptions={filterOptions}
          allFilters={allFilters}
          filters={filters}
          onChangeFilter={handleChangeFilter}
          filtersType={"business_accounts"}
          showClearBtn={!isFiltersDefault}
          onClearFilters={onClear}
          addGlobalFilter={props.addGlobalFilter}
          removeGlobalFilter={props.removeGlobalFilter}
        />

        <Space style={{ flexShrink: 0 }}>
          <GlobalFilterTestData setPageNumber={setPageNumber} />

          {roleId !== adminRoleId && (
            <ExportDropdown loading={exportFetching} onExport={handleExport} isAdmin={true} />
          )}

          <ManageColumn myColumns={myColumns} onChangeColumns={handleChangeColumns} />
        </Space>
      </Flex>

      <Table
        columns={getTableColumns}
        loading={fetching}
        dataSource={results}
        scroll={tableSettings.scroll}
        onChange={handleChangeTable}
        pagination={tableSettings.pagination}
        showSizeChanger={false}
        //withOutPagination={true}
        locale={{
          emptyText: !statistics?.["users_counter"] ? (
            <EmptyComponent type="business_accounts_admin" />
          ) : (
            <EmptyComponent type="search">
              <Button
                className="flex"
                icon={<Icon type={"menu_search"} />}
                onClick={props.clearBusinessAccountsFilters}
              >
                {t("Clear search")}
              </Button>
            </EmptyComponent>
          )
        }}
      />

      <Modal
        classNames={{ content: "clear-bg" }}
        {...(isMobile ? { transitionName: "" } : null)}
        open={!!selectBusinessId}
        getContainer={() => document.getElementById("main-layout")}
        title={
          <Flex justify={"space-between"} style={{ paddingRight: 48 }}>
            <Text>{`${t("Alerts sent")}: ${chartData?.["total_value"] || "..."}`}</Text>

            <Space.Compact size={"large"}>
              <Button
                className={cls("compact-btn", {
                  active: selectPeriod === "7 days"
                })}
                onClick={() => handleChangePeriod("7 days")}
              >
                {t("7 days")}
              </Button>
              <Button
                className={cls("compact-btn", {
                  active: selectPeriod === "30 days"
                })}
                onClick={() => handleChangePeriod("30 days")}
              >
                {t("30 days")}
              </Button>
              <Button
                className={cls("compact-btn", {
                  active: selectPeriod === "all"
                })}
                onClick={() => handleChangePeriod("all")}
              >
                {t("All time")}
              </Button>
              <div>
                <DropdownDateBlock
                  id={"customPeriod"}
                  customOverlayClassName={"dashboard-filters"}
                  label={"Custom"}
                  iconType={"calendar_days"}
                  dropdownFilters={getCalculatedPeriod(selectPeriod)}
                  presetId={selectPeriod?.presetId}
                  setDropdownFilters={handleChangeCustomDate}
                  inactive={selectPeriod?.type !== "custom"}
                />
              </div>
            </Space.Compact>
          </Flex>
        }
        centered={!isMobile}
        //closeIcon={<Icon type="close_modal"/>}
        footer={null}
        closable="true"
        onCancel={() => setSelectBusinessId(null)}
        destroyOnClose
        width={1160}
      >
        <Spin spinning={chartLoading}>
          <Chart
            height={isMobile ? null : "280px"}
            type={"alerts"}
            loading={chartLoading}
            data={chartData?.chart}
          />
        </Spin>
      </Modal>

      {modalType?.type === "comments_ba_page" ? (
        <CommentModal
          modalType={modalType}
          setModalType={setModalType}
          modalTabKey={modalTabKey}
          setModalTabKey={handleModalTabChange}
          handleSaveForm={handleSaveCommentForm}
          onCommentDelete={onCommentDelete}
          onDeleteAttachment={onDeleteAttachment}
          userInfoComment={modalTabKey === "business_accounts_owner" ? baCommentData : modalType}
          userCommentData={activeCommentData}
          formRef={formRef}
          commentDataLoading={fetchingBaComment}
          onClose={() => {
            setModalTabKey("business_account")
            props.clearBaCommentsData()
          }}
        />
      ) : null}
    </div>
  )
}

const mapStateToProps = (state) => ({
  businessAccountId: state.auth.userInfo.current_business_account_id,
  isAdmin: state.auth.isAdmin,
  roleId: state.auth.userInfo?.role?.id,
  isMobile: state.nav.isMobile,

  visibleFiltersAdmin: state.businessAccounts.visibleFiltersAdmin,

  defaultColumns: state.businessAccounts.defaultColumns,
  defaultColumnsAdmin: state.businessAccounts.defaultColumnsAdmin,

  columns: state.businessAccounts.columns,
  columnsAdmin: state.businessAccounts.columnsAdmin,

  fetching: state.businessAccounts.fetching,
  result: state.businessAccounts.result,

  filters: state.businessAccounts.filters,
  filterOptions: state.businessAccounts.filterOptions,

  exportFetching: state.businessAccounts.exportFetching,
  exportResult: state.businessAccounts.exportResult,

  chartLoading: state.businessAccounts.chartLoading,
  chartData: state.businessAccounts.chartData,
  statistics: state.businessAccounts.statistics,
  test_data: state.globalFilters.testData,

  baCommentData: state.businessAccounts.baCommentData,
  fetchingBaComment: state.businessAccounts.fetchingBaComment
})

const mapDispatchToProps = (dispatch) => ({
  getBusinessAccountsFilterOptions: () =>
    dispatch(Creators.getBusinessAccountsFilterOptionsRequest()),

  getAdminBusinessAccounts: (params) => dispatch(Creators.getAdminBusinessAccountsRequest(params)),
  deleteUserBusinessAccount: (id) => dispatch(Creators.deleteUserBusinessAccountRequest(id)),
  getAlertsChart: (params) => dispatch(Creators.getAlertsChartRequest(params)),
  getBusinessAccountsExport: (businessId) =>
    dispatch(Creators.getBusinessAccountsExportRequest(businessId)),
  clearExportResult: () => dispatch(Creators.businessAccountsSetProp("exportResult", null)),
  changeColumns: (key, columns) => dispatch(Creators.businessAccountsSetProp(key, columns)),
  changeFilters: (filters) => dispatch(Creators.businessAccountsSetProp("filters", filters)),
  changeVisibleFilters: (visibleFiltersType, filters) =>
    dispatch(Creators.businessAccountsSetProp(visibleFiltersType, filters)),
  clearBusinessAccountsFilters: () => dispatch(Creators.clearBusinessAccountsFilters()),
  changeBusinessAccount: (newBusinessAccountId) =>
    dispatch(
      userSave({
        current_business_account_id: newBusinessAccountId
      })
    ),
  clearGlobalFilters: () => dispatch(GlobalCreators.clearGlobalFilters()),
  addGlobalFilter: (key, value) => dispatch(GlobalCreators.addGlobalFilter(key, value)),
  removeGlobalFilter: (key) => dispatch(GlobalCreators.removeGlobalFilter(key)),
  getBaCommentData: (params) => dispatch(Creators.getBaCommentDataRequest(params)),
  resetBaCommentData: () => dispatch(Creators.businessAccountsSetProp("baCommentData", null)),
  saveBaComment: (params) => dispatch(Creators.saveBaCommentRequest(params)),
  deleteBaComment: (params) => dispatch(Creators.deleteBaCommentRequest(params)),
  deleteBaCommentAttachment: (params) =>
    dispatch(Creators.deleteBaCommentAttachmentRequest(params)),
  clearBaCommentsData: () => dispatch(Creators.clearBaCommentsData())
})

export default connect(mapStateToProps, mapDispatchToProps)(BusinessAccountsPage)
